import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Button, Card, CardContent, CircularProgress, Stack, Step, StepButton, Stepper, Typography, Alert,
  useMediaQuery, useTheme,
} from '@mui/material';
import PageShell from '../../components/layout/PageShell';
import ZoneLocationStep from './components/ZoneLocationStep';
import ZoneSchedulePricingStep from './components/ZoneSchedulePricingStep';
import ZoneSurgeStep from './components/ZoneSurgeStep';
import ZoneFarePreview from './components/ZoneFarePreview';
import ZoneDeliveryPartnerStep from './components/ZoneDeliveryPartnerStep';
import ZoneOnDemandPricingStep, { OnDemandZonePricingSummary } from './components/ZoneOnDemandPricingStep';
import {
  emptyZoneForm, formToApiPayload, formHasDeliveryPartnerPricing, formHasSelectedPorter, formNeedsSchedulePricing,
  formHasSelectedOnDemand, isOnDemandPricingRow,
  getStep0FieldErrors, hydrateZoneFormFromDetail, activeServicePricing, activeVehiclePricing,
  configuredTransportVehicleCount, configuredZoneServiceCount, reconcileZoneFormCatalog, resolveZoneServicePool,
  syncZoneFormWithSelectedServices,
  getZonePartnerPricingErrors, getZoneSurgePricingErrors, getSurgeRuleNameErrors, getSurgeTimeErrors,
  FALLBACK_VEHICLES, zoneManagementPath, buildServicePricingRow, safeZoneReturnTo,
  filterServicesForZoneScope, stripTransportFromDeliveryScopedForm,
  type HubServiceOption, type ZoneFormState, type VehicleTypeOption,
} from './zoneTypes';
import { createZone, getZone, listPrimaryZones, listZones, updateZone } from '../../services/zoneService';
import { listHubServices } from '../../services/serviceHubService';
import { getMasters } from '../../services/platform/masterService';
import api from '../../services/api';
import type { ServiceHubType } from '../../constants/serviceHubs';
import { formatMoney } from '../../utils/currency';

const BASE_STEPS = [
  { key: 'location', title: 'Service Location', subtitle: 'Map & services' },
  { key: 'ride_now', title: 'Ride Now', subtitle: 'Pricing' },
  { key: 'ride_later', title: 'Ride Later', subtitle: 'Pricing' },
  { key: 'partner', title: 'Partner Pricing', subtitle: 'Fee + incentives' },
  { key: 'services', title: 'Service Pricing', subtitle: 'Visit charge' },
  { key: 'surge', title: 'Surge Price', subtitle: 'Time & days' },
  { key: 'review', title: 'Review', subtitle: 'Save zone' },
] as const;

const ALL_HUBS: ServiceHubType[] = ['transport', 'delivery', 'on_demand'];

function parseScopeHub(raw: string | null): ServiceHubType | undefined {
  if (raw === 'transport' || raw === 'delivery' || raw === 'on_demand') return raw;
  return undefined;
}

export default function ZoneWizardPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isEdit = !!id;
  const scopeHub = parseScopeHub(searchParams.get('hub'));
  const scopeServiceSlug = (searchParams.get('service') || '').trim() || undefined;
  const hubsToLoad = scopeHub ? [scopeHub] : ALL_HUBS;
  const zonesListPath = zoneManagementPath({ hub: scopeHub, service: scopeServiceSlug });
  const backPath = safeZoneReturnTo(searchParams.get('returnTo'), zonesListPath);
  const goBack = () => navigate(backPath);
  const [step, setStep] = useState(0);
  const [maxStepReached, setMaxStepReached] = useState(0);
  const [form, setForm] = useState<ZoneFormState>(emptyZoneForm());
  const [formReady, setFormReady] = useState(!isEdit);
  const [showStep0Errors, setShowStep0Errors] = useState(false);
  const [showSurgeErrors, setShowSurgeErrors] = useState(false);
  const [saveWarning, setSaveWarning] = useState('');
  const hydratedRef = useRef(false);
  const scopedDefaultsApplied = useRef(false);

  const { data: loadedServices = [], isLoading: servicesLoading } = useQuery({
    queryKey: ['zone-wizard-services', hubsToLoad.join(',')],
    queryFn: async () => {
      const results = await Promise.allSettled(hubsToLoad.map((h) => listHubServices(h)));
      return results.flatMap((r) => (r.status === 'fulfilled' ? r.value : [])).map((s) => ({
        _id: s._id,
        name: s.name,
        slug: s.slug,
        hubType: s.hubType as ServiceHubType,
        themeColor: s.themeColor,
      })) as HubServiceOption[];
    },
    retry: 1,
  });

  const onDemandScoped = scopeHub === 'on_demand';
  /** Delivery and Services scopes never show transport vehicles, bidding or ride pricing. */
  const transportHidden = scopeHub === 'delivery' || onDemandScoped;
  const deliveryScoped = scopeHub === 'delivery';
  const foodScoped = scopeServiceSlug === 'food-delivery' || scopeServiceSlug === 'grocery-delivery';

  const allServices = useMemo(
    () => filterServicesForZoneScope(loadedServices, {
      hub: scopeHub,
      serviceSlug: scopeServiceSlug,
    }),
    [loadedServices, scopeHub, scopeServiceSlug],
  );

  const { data: primaryZones = [] } = useQuery({
    queryKey: ['primary-zones'],
    queryFn: listPrimaryZones,
    retry: 1,
  });

  const { data: vehicleTypes = transportHidden ? [] : FALLBACK_VEHICLES } = useQuery({
    queryKey: ['zone-wizard-vehicle-types'],
    queryFn: async () => {
      const res = await getMasters('vehicle_types', { limit: 50 }).catch(() => ({ data: [] as Array<{ code: string; name: string }> }));
      const rows = (res.data || []) as Array<{ code: string; name: string }>;
      return rows.length ? rows.map((r) => ({ code: r.code, name: r.name })) : FALLBACK_VEHICLES;
    },
    retry: 1,
    enabled: !transportHidden,
  });

  const servicePool = useMemo(
    () => resolveZoneServicePool(form, allServices, primaryZones),
    [form.zoneLevel, form.primaryZoneId, allServices, primaryZones],
  );

  const { data: allZonesForUniqueness = [] } = useQuery({
    queryKey: ['zones-uniqueness'],
    queryFn: async () => {
      const res = await listZones({ limit: 500 });
      return res.rows.map((z) => ({ _id: z._id, name: z.name, code: z.code }));
    },
    retry: 1,
  });

  // Auto-select scoped delivery service on create
  useEffect(() => {
    if (isEdit || scopedDefaultsApplied.current || !allServices.length) return;
    if (!scopeServiceSlug && !deliveryScoped) return;
    const preferred = scopeServiceSlug
      ? allServices.find((s) => s.slug === scopeServiceSlug)
      : allServices.find((s) => s.hubType === 'delivery');
    if (!preferred) return;
    scopedDefaultsApplied.current = true;
    setForm((prev) => {
      if (prev.selectedServiceIds.includes(preferred._id)) return prev;
      const pricingRow = prev.servicePricing.find((sp) => sp.serviceId === preferred._id)
        || buildServicePricingRow(preferred);
      return syncZoneFormWithSelectedServices({
        ...prev,
        selectedServiceIds: [preferred._id],
        servicePricing: [
          pricingRow,
          ...prev.servicePricing.filter((sp) => sp.serviceId !== preferred._id),
        ],
      }, allServices);
    });
  }, [isEdit, allServices, scopeServiceSlug, deliveryScoped]);

  const step0Opts = {
    existingZones: allZonesForUniqueness,
    excludeZoneId: isEdit ? id : undefined,
  };

  const {
    data: editPayload,
    isLoading: zoneLoading,
    isFetching: zoneFetching,
    isError: zoneLoadError,
    error: zoneLoadErr,
  } = useQuery({
    queryKey: ['zone-edit', id],
    queryFn: async () => {
      const [detail, vehicleRes, bidConfigRes] = await Promise.all([
        getZone(id!),
        getMasters('vehicle_types', { limit: 50 }).catch(() => ({ data: [] as Array<{ code: string; name: string }> })),
        api.get(`/pricing/bidding/zones/${id}/bid-config`).catch(() => null),
      ]);
      const rows = (vehicleRes.data || []) as Array<{ code: string; name: string }>;
      const vehicleTypes: VehicleTypeOption[] = rows.length
        ? rows.map((r) => ({ code: r.code, name: r.name }))
        : FALLBACK_VEHICLES;
      const bidConfig = bidConfigRes?.data?.data as { allowDriverCounterOffer?: boolean; autoAccept?: boolean } | null;
      return { detail, vehicleTypes, bidConfig };
    },
    enabled: isEdit && !!id,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: 1,
  });

  useEffect(() => {
    if (!id) return;
    hydratedRef.current = false;
    setFormReady(false);
    setForm(emptyZoneForm());
  }, [id]);

  useEffect(() => {
    if (!isEdit || !editPayload || hydratedRef.current || servicesLoading || zoneFetching) return;
    const vehicleCatalog = transportHidden ? [] : editPayload.vehicleTypes;
    let hydrated = hydrateZoneFormFromDetail(editPayload.detail, allServices, vehicleCatalog);
    hydrated = {
      ...hydrated,
      allowDriverCounterOffer: transportHidden
        ? false
        : (editPayload.bidConfig?.allowDriverCounterOffer ?? true),
      autoAcceptLowestBid: transportHidden
        ? false
        : (editPayload.bidConfig?.autoAccept ?? false),
    };
    if (transportHidden) {
      hydrated = stripTransportFromDeliveryScopedForm(hydrated);
    }
    // Keep only services available in this scoped catalog (food-only when opened from Food).
    const poolIds = new Set(allServices.map((s) => s._id));
    const scopedIds = hydrated.selectedServiceIds.filter((id) => poolIds.has(id));
    if (scopeServiceSlug) {
      const locked = allServices.find((s) => s.slug === scopeServiceSlug);
      if (locked && !scopedIds.includes(locked._id)) scopedIds.push(locked._id);
    }
    const nextPricing = hydrated.servicePricing.filter((sp) => scopedIds.includes(sp.serviceId));
    if (scopeServiceSlug) {
      const locked = allServices.find((s) => s.slug === scopeServiceSlug);
      if (locked && !nextPricing.some((sp) => sp.serviceId === locked._id)) {
        nextPricing.push(buildServicePricingRow(locked));
      }
    }
    setForm(syncZoneFormWithSelectedServices({
      ...hydrated,
      selectedServiceIds: scopedIds.length ? scopedIds : (allServices[0] ? [allServices[0]._id] : []),
      servicePricing: nextPricing.length
        ? nextPricing
        : (allServices[0] ? [buildServicePricingRow(allServices[0])] : []),
    }, allServices));
    setFormReady(true);
    hydratedRef.current = true;
  }, [isEdit, editPayload, allServices, servicesLoading, zoneFetching, transportHidden, scopeServiceSlug]);

  const selectedServiceKey = form.selectedServiceIds.join('|');
  const selectedVehicleKey = form.selectedVehicleTypeCodes.join('|');
  useEffect(() => {
    if (!allServices.length) return;
    if (!transportHidden && !vehicleTypes.length) return;
    if (isEdit && !formReady) return;
    setForm((prev) => {
      let next = reconcileZoneFormCatalog(
        prev,
        allServices,
        servicePool,
        transportHidden ? [] : vehicleTypes,
      );
      if (transportHidden) next = stripTransportFromDeliveryScopedForm(next);
      return next;
    });
  }, [
    selectedServiceKey,
    selectedVehicleKey,
    allServices,
    servicePool,
    vehicleTypes,
    isEdit,
    formReady,
    form.zoneLevel,
    form.primaryZoneId,
    transportHidden,
  ]);

  // Delivery Food/Grocery: Partner Pricing pattern only (never Ride Now / Ride Later transport steps).
  const hasPartnerStep = deliveryScoped
    || formHasDeliveryPartnerPricing(form, allServices)
    || foodScoped;
  const needsSchedulePricing = transportHidden ? false : formNeedsSchedulePricing(form);
  // Services (on-demand): visit charge + minimum package price only; surge is not applied to Services bookings.
  const hasServicesStep = formHasSelectedOnDemand(form, allServices);
  const tripServicePricing = activeServicePricing(form).filter((sp) => !isOnDemandPricingRow(sp));
  const STEPS = BASE_STEPS.filter((s) => {
    if (s.key === 'partner' && !hasPartnerStep) return false;
    if ((s.key === 'ride_now' || s.key === 'ride_later') && !needsSchedulePricing) return false;
    if (s.key === 'services' && !hasServicesStep) return false;
    if (s.key === 'surge' && onDemandScoped) return false;
    return true;
  });
  const currentStep = STEPS[step];

  useEffect(() => {
    if (step >= STEPS.length) setStep(Math.max(0, STEPS.length - 1));
  }, [STEPS.length, step]);

  useEffect(() => {
    if (isEdit && formReady) {
      setMaxStepReached(Math.max(0, STEPS.length - 1));
    }
  }, [isEdit, formReady, STEPS.length]);

  const saveMut = useMutation({
    mutationFn: async () => {
      setSaveWarning('');
      const payload: Record<string, unknown> = { ...formToApiPayload(form) };
      if (isEdit && scopeHub) {
        // Hub-scoped edit: leave other hubs' services, pricing, surge rules and bidding untouched.
        payload.scopeServiceIds = allServices.map((s) => s._id);
        delete payload.biddingEnabled;
      }
      const result = isEdit && id
        ? await updateZone(id, payload)
        : await createZone(payload);
      const zoneId = isEdit ? id! : result.zone._id;
      if (zoneId && form.biddingEnabled && !transportHidden) {
        await api.put(`/pricing/bidding/zones/${zoneId}/bid-config`, {
          enabled: true,
          allowDriverCounterOffer: form.allowDriverCounterOffer,
          autoAccept: form.autoAcceptLowestBid,
        });
      } else if (zoneId && isEdit && !form.biddingEnabled && !transportHidden) {
        await api.put(`/pricing/bidding/zones/${zoneId}/bid-config`, {
          enabled: false,
          allowDriverCounterOffer: form.allowDriverCounterOffer,
          autoAccept: form.autoAcceptLowestBid,
        }).catch(() => undefined);
      }
      return result;
    },
    onSuccess: (result) => {
      const zoneId = result.zone._id;
      qc.invalidateQueries({ queryKey: ['zone-edit', zoneId] });
      qc.invalidateQueries({ queryKey: ['zone-detail', zoneId] });
      qc.invalidateQueries({ queryKey: ['operational-zones'] });
      navigate(backPath);
    },
    onError: (err) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || 'Failed to save zone.';
      if (msg.toLowerCase().includes('bidding')) {
        setSaveWarning(msg);
      }
    },
  });

  const step0Errors = getStep0FieldErrors(form, allServices, step0Opts);
  const step0BlockReason = Object.values(step0Errors)[0] || null;
  const surgeNameErrors = getSurgeRuleNameErrors(form);
  const surgeTimeErrors = getSurgeTimeErrors(form);
  const surgeBlockReason = getZoneSurgePricingErrors(form);
  const editLoading = isEdit && (!formReady || zoneLoading || zoneFetching || servicesLoading);

  const canNavigateToStep = (targetIndex: number) => {
    if (editLoading) return false;
    if (isEdit && formReady) return true;
    return targetIndex <= maxStepReached;
  };

  const goToStep = (targetIndex: number) => {
    if (!canNavigateToStep(targetIndex) || targetIndex === step) return;
    if (targetIndex !== 0) setShowStep0Errors(false);
    if (currentStep?.key !== 'surge') setShowSurgeErrors(false);
    setStep(targetIndex);
  };

  const goNext = () => {
    if (step === 0) {
      const errs = getStep0FieldErrors(form, allServices, step0Opts);
      if (Object.keys(errs).length || !form.selectedServiceIds.length) {
        setShowStep0Errors(true);
        return;
      }
    }
    if (currentStep?.key === 'partner') {
      const partnerErr = getZonePartnerPricingErrors(form, allServices);
      if (partnerErr) {
        setSaveWarning(partnerErr);
        return;
      }
    }
    if (currentStep?.key === 'surge') {
      const surgeErr = getZoneSurgePricingErrors(form);
      if (surgeErr) {
        setShowSurgeErrors(true);
        setSaveWarning(surgeErr);
        return;
      }
    }
    setSaveWarning('');
    setShowSurgeErrors(false);
    setStep((s) => {
      const next = Math.min(s + 1, STEPS.length - 1);
      setMaxStepReached((m) => Math.max(m, next));
      return next;
    });
  };

  const saveZone = () => {
    const errs = getStep0FieldErrors(form, allServices, step0Opts);
    if (Object.keys(errs).length) {
      setShowStep0Errors(true);
      setStep(0);
      return;
    }
    const partnerErr = getZonePartnerPricingErrors(form, allServices);
    if (partnerErr) {
      setSaveWarning(partnerErr);
      const partnerIndex = STEPS.findIndex((s) => s.key === 'partner');
      if (partnerIndex >= 0) setStep(partnerIndex);
      return;
    }
    const surgeErr = onDemandScoped ? null : getZoneSurgePricingErrors(form);
    if (surgeErr) {
      setShowSurgeErrors(true);
      setSaveWarning(surgeErr);
      const surgeIndex = STEPS.findIndex((s) => s.key === 'surge');
      if (surgeIndex >= 0) setStep(surgeIndex);
      return;
    }
    setShowSurgeErrors(false);
    setSaveWarning('');
    saveMut.mutate();
  };

  return (
    <PageShell>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>{isEdit ? 'Edit Zone' : 'Create Zone'}</Typography>
          <Typography variant="body2" color="text.primary" fontWeight={600} sx={{ opacity: 0.9 }}>
            {foodScoped
              ? `Food Delivery zone wizard — Partner Pricing pattern for ${scopeServiceSlug === 'grocery-delivery' ? 'Grocery' : 'Food'} Delivery`
              : deliveryScoped
                ? 'Delivery zone wizard — Partner Pricing for food, grocery, and porter services'
                : onDemandScoped
                  ? 'Services zone wizard — zone visit charge and minimum package price for on-demand services'
                  : 'Multi-service zone wizard with vehicle-wise transport pricing'}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={goBack}>
          {backPath.startsWith('/hubs/') ? 'Back' : 'Back to zones'}
        </Button>
      </Stack>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ px: { xs: 1, sm: 2 } }}>
          <Stepper
            activeStep={step}
            alternativeLabel={!isMobile}
            orientation={isMobile ? 'vertical' : 'horizontal'}
          >
            {STEPS.map((s, index) => (
              <Step
                key={s.key}
                completed={(isEdit && formReady && index !== step) || index < step}
              >
                <StepButton
                  onClick={() => goToStep(index)}
                  disabled={!canNavigateToStep(index)}
                  sx={{ py: { xs: 0.5, sm: 1 } }}
                >
                  <Typography variant="body2" fontWeight={600}>{s.title}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{s.subtitle}</Typography>
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {zoneLoadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(zoneLoadErr as { response?: { data?: { message?: string } } })?.response?.data?.message
            || 'Failed to load zone for editing.'}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          {editLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }} spacing={2}>
              <CircularProgress size={36} />
              <Typography color="text.secondary">Loading zone data…</Typography>
            </Stack>
          ) : (
            <>
              {currentStep?.key === 'location' && (
                <ZoneLocationStep
                  form={form}
                  setForm={setForm}
                  allServices={allServices}
                  primaryZones={primaryZones}
                  showErrors={showStep0Errors}
                  fieldErrors={step0Errors}
                  existingZones={allZonesForUniqueness}
                  excludeZoneId={isEdit ? id : undefined}
                  scopeHub={scopeHub}
                  lockedServiceSlug={scopeServiceSlug}
                />
              )}
              {currentStep?.key === 'ride_now' && (
                <>
                  <ZoneSchedulePricingStep form={form} setForm={setForm} scheduleType="ride_now" unit={form.unit} currency={form.currency} />
                  <Box sx={{ mt: 2 }}><ZoneFarePreview servicePricing={tripServicePricing} vehiclePricing={activeVehiclePricing(form)} scheduleType="ride_now" currency={form.currency} /></Box>
                </>
              )}
              {currentStep?.key === 'ride_later' && (
                <>
                  <ZoneSchedulePricingStep form={form} setForm={setForm} scheduleType="ride_later" unit={form.unit} currency={form.currency} />
                  <Box sx={{ mt: 2 }}><ZoneFarePreview servicePricing={tripServicePricing} vehiclePricing={activeVehiclePricing(form)} scheduleType="ride_later" currency={form.currency} /></Box>
                </>
              )}
              {currentStep?.key === 'partner' && (
                <ZoneDeliveryPartnerStep
                  form={form}
                  setForm={setForm}
                  allServices={allServices}
                  unit={form.unit}
                  currency={form.currency}
                />
              )}
              {currentStep?.key === 'services' && (
                <ZoneOnDemandPricingStep form={form} setForm={setForm} currency={form.currency} />
              )}
              {currentStep?.key === 'surge' && (
                <ZoneSurgeStep
                  form={form}
                  setForm={setForm}
                  allServices={allServices}
                  currency={form.currency}
                  showErrors={showSurgeErrors}
                  nameErrors={surgeNameErrors}
                  timeErrors={surgeTimeErrors}
                />
              )}
              {currentStep?.key === 'review' && (
                <Box>
                  <Typography variant="h6" fontWeight={700}>{form.name} ({form.code})</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {form.polygon.length} map points · {configuredZoneServiceCount(form, allServices, servicePool)} services
                    {!transportHidden && (
                      <> · {configuredTransportVehicleCount(form, allServices, vehicleTypes)} transport vehicles</>
                    )}
                    {!onDemandScoped && <>{' · '}{form.surgePricing.length} surge rules</>}
                    {!transportHidden && form.biddingEnabled && ` · counter ${form.allowDriverCounterOffer ? 'on' : 'off'} · auto-accept ${form.autoAcceptLowestBid ? 'on' : 'off'}`}
                    {hasPartnerStep && ` · partner base ${formatMoney(form.deliveryPartnerPricing.baseAmount || (formHasSelectedPorter(form, allServices) ? form.porterPartnerPricing.baseAmount : 0), form.currency)}`}
                    {formHasSelectedPorter(form, allServices) && form.porterMasters.vehicleIds.length > 0 && ` · ${form.porterMasters.vehicleIds.length} porter vehicles`}
                  </Typography>
                  {hasServicesStep && (
                    <Box sx={{ p: 2, mb: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>Service pricing</Typography>
                      <OnDemandZonePricingSummary form={form} currency={form.currency} />
                    </Box>
                  )}
                  {(tripServicePricing.length > 0 || (!transportHidden && activeVehiclePricing(form).length > 0)) && (
                    <ZoneFarePreview
                      servicePricing={tripServicePricing}
                      vehiclePricing={transportHidden ? [] : activeVehiclePricing(form)}
                      scheduleType="ride_now"
                      currency={form.currency}
                      surgeMultiplier={1.3}
                    />
                  )}
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {showStep0Errors && step0BlockReason && !editLoading && (
        <Alert severity="error" sx={{ mb: 2 }}>{step0BlockReason}</Alert>
      )}

      <Stack direction={{ xs: 'column-reverse', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
        <Button
          disabled={editLoading}
          onClick={() => {
            if (step === 0) goBack();
            else goToStep(step - 1);
          }}
          fullWidth={isMobile}
        >
          Back
        </Button>
        {step < STEPS.length - 1 ? (
          <Button
            variant="contained"
            onClick={goNext}
            disabled={editLoading || (step === 0 && !form.selectedServiceIds.length)}
            fullWidth={isMobile}
          >
            Continue
          </Button>
        ) : (
          <Button variant="contained" onClick={saveZone} disabled={saveMut.isPending || editLoading} fullWidth={isMobile}>
            {isEdit ? 'Update Zone' : 'Create Zone'}
          </Button>
        )}
      </Stack>
      {saveMut.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {(saveMut.error as { response?: { data?: { message?: string } } })?.response?.data?.message
            || 'Failed to save zone. Check zone code is unique and polygon has at least 3 points.'}
        </Alert>
      )}
      {saveWarning && (
        <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setSaveWarning('')}>
          {saveWarning}
        </Alert>
      )}
    </PageShell>
  );
}
